import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import React from "react";
import { Descendant } from "slate";

import { PageV1 } from "@/components/Page-V1";
import { ExperienceProvider } from "@/context/ExperienceContext";
import type { BlockPageProps } from "@/types/Block";
import { Experience, getExperience } from "@/utils/experience";
import { getIsProduction } from "@/utils/production";
import { fetchProfileText, getProfileTextIdsAndRemoveUndefinedValues } from "@/utils/profileText";

export const getServerSideProps: GetServerSideProps<{
  content: BlockPageProps | null;
  experience: Experience | null;
  isProduction: boolean;
  textBlocks: Record<string, Descendant[]> | null;
}> = async ({ req }) => {
  const experience = getExperience(req);
  const isProduction = getIsProduction();
  const profile = experience?.profile;

  let content: BlockPageProps | undefined;
  let textBlocks: Record<string, Descendant[]> | null;

  try {
    const contentModule = profile
      ? await import(`../../profiles${profile}`)
      : Promise.resolve(undefined);
    const ids: string[] = [];
    content = getProfileTextIdsAndRemoveUndefinedValues(contentModule.default, ids);
    textBlocks = await (profile
      ? fetchProfileText(experience.country, experience.language, ids).catch(() => null)
      : Promise.resolve(null));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Invalid profile path:", e);
    return {
      notFound: true
    };
  }

  if (
    !content ||
    (isProduction && !experience?.isProduction) ||
    (isProduction &&
      !!experience?.requiresReferer.length &&
      !experience?.requiresReferer.find((referer) => req.headers.referer?.includes(referer)))
  ) {
    return {
      props: getProfileTextIdsAndRemoveUndefinedValues(
        {
          content: null,
          experience,
          isProduction,
          textBlocks: null
        },
        []
      )
    };
  }

  return {
    props: getProfileTextIdsAndRemoveUndefinedValues(
      {
        content,
        experience,
        isProduction,
        textBlocks: textBlocks
      },
      []
    )
  };
};

const NextJsContentPage = ({
  content,
  experience,
  textBlocks
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  return (
    <ExperienceProvider experience={experience || {}}>
      <PageV1 content={content} textBlocks={textBlocks} />
    </ExperienceProvider>
  );
};

export default NextJsContentPage;
